/**
 * @prettier
 */

import React, { useCallback } from 'react';
import cx from 'classnames';
import { connect } from 'react-redux';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import arrowIcon from 'assets/images/invitations/single-sample-modal/arrow.svg';
import { getCardCatalogBusy } from 'cards/selectors/cardCatalogSelector';
import { getMultiSampleState } from 'cards/selectors/singlesample';
import { setMultiSampleEnabled as setMultiSampleEnabledAction } from 'cards/actions/singlesample';
import { Toggle } from '@zola/zola-ui/src/components/Toggle';
import MultiSampleTileBg from 'assets/images/invitations/single-sample-modal/multi-sample-plp-tile-bg.svg';
import MultiSampleBg from 'assets/images/invitations/single-sample-modal/multi-sample-bg.png';
import MobileMultiSampleBg from 'assets/images/invitations/single-sample-modal/mobile-multi-sample-bg.jpg';
import { getIsMobileOrTablet } from 'paper/hooks/isMobileOrTablet';
import { Link } from '@zola/zola-ui/src/components/Link';
import { trackEvent } from '../../../../../util/trackingHelper';
import styles from './styles.module.less';
import FeatureFlags from '../../../../../util/featureFlags';

const CTA_CLICKED = 'CTA Clicked';

const Arrow = () => (
  <div className={styles.tileArrowBox}>
    <img className={styles.tileArrow} alt="" src={arrowIcon} />
  </div>
);

export const PLPTileBase = ({
  cardType,
  plpBusy,
  multiSampleState,
  setMultiSampleEnabled,
  isMobile = false,
}) => {
  const isMobileOrTablet = getIsMobileOrTablet();

  const toggleEnabledState = useCallback(() => {
    if (!multiSampleState.enabled) {
      trackEvent(CTA_CLICKED, {
        business_unit: BUSINESS_UNIT.INVITATIONS,
        cta_type: 'TOGGLE',
        cta: 'PLP_SAMPLE_TILE_TOGGLE',
      });
    }
    setMultiSampleEnabled(cardType, !multiSampleState.enabled);
  }, [cardType, multiSampleState.enabled, setMultiSampleEnabled]);

  const enableSamples = useCallback(() => {
    if (!multiSampleState.enabled) {
      trackEvent(CTA_CLICKED, {
        business_unit: BUSINESS_UNIT.INVITATIONS,
        cta_type: 'BUTTON',
        cta: 'SELECT_YOUR_DESIGNS',
      });
    }
    setMultiSampleEnabled(cardType, true);
  }, [cardType, multiSampleState.enabled, setMultiSampleEnabled]);

  if (plpBusy) {
    return null;
  }

  // New redesigned sample tile (currently desktop only)
  if (FeatureFlags.get('cardsMultiSampleTile')) {
    return (
      <>
        {isMobileOrTablet ? (
          <div className={styles.plpTileBoxNew}>
            <div
              className={styles.plpTileMobileImg}
              style={{ backgroundImage: `url(${MobileMultiSampleBg})` }}
            />
            <div className={styles.plpTileTitleNew}>Get 5 Free Personalized Samples</div>
            {!multiSampleState.enabled && (
              <Link arrow role="button" className={styles.plpTileLink} onClick={enableSamples}>
                Select Designs
              </Link>
            )}
          </div>
        ) : (
          <div
            className={styles.plpTileBoxNew}
            style={{ backgroundImage: `url(${MultiSampleBg})` }}
          >
            <div className={styles.plpTileTitleNew}>How to get free personalized samples:</div>
            <div className={styles.plpTileListBoxNew}>
              <div className={styles.plpTileListNew}>
                <div className={styles.plpTileListItem}>
                  <div className={styles.plpTileListNumber}>1</div>
                  <div className={styles.plpTileListText}>Choose up to five designs.</div>
                </div>
                <div className={styles.plpTileListItem}>
                  <div className={styles.plpTileListNumber}>2</div>
                  <div className={styles.plpTileListText}>Add a photo and your details.</div>
                </div>
                <div className={styles.plpTileListItem}>
                  <div className={styles.plpTileListNumber}>3</div>
                  <div className={styles.plpTileListText}>
                    Get your samples (with free shipping!).
                  </div>
                </div>
              </div>
            </div>
            {!multiSampleState.enabled && (
              <div>
                <ButtonV2 variant="primary" onClick={enableSamples}>
                  Select your designs
                </ButtonV2>
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  // Old sample tile design
  return (
    <div className={styles.plpTileBox} style={{ backgroundImage: `url(${MultiSampleTileBg})` }}>
      <div className={styles.plpTileTitle}>
        {isMobile && multiSampleState.enabled
          ? `Pick ${multiSampleState.count.limit} free samples`
          : 'Want Free Samples?'}{' '}
        {isMobile && (
          <div className={cx(styles.toggle, styles.inline)}>
            <Toggle
              id="multi-samples-toggle-tile"
              ariaLabel="Select multi sample"
              withIcons
              input={{
                value: multiSampleState.enabled,
                onChange: toggleEnabledState,
              }}
            />
          </div>
        )}
      </div>
      <div className={styles.plpTileListBox}>
        <ol className={styles.plpTileList}>
          {isMobile && (
            <li>
              Turn on the samples toggle. <Arrow />
            </li>
          )}
          <li>Choose up to five designs.</li>
          <li>Add a photo and your details.</li>
          <li>Get your samples (with free shipping!).</li>
        </ol>
      </div>
      {!isMobile && !multiSampleState.enabled && (
        <ButtonV2 variant="secondary" onClick={enableSamples}>
          Select your designs
        </ButtonV2>
      )}
    </div>
  );
};

export const mapStateToProps = state => ({
  plpBusy: getCardCatalogBusy(state),
  multiSampleState: getMultiSampleState(state),
});

export const mapDispatchToProps = dispatch => ({
  setMultiSampleEnabled: (cardType, enabled) =>
    dispatch(setMultiSampleEnabledAction(cardType, enabled)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PLPTileBase);
